import { forwardRef } from 'react';
import clsx from 'clsx';
import { Spinner } from './Spinner';
import { FocusRing } from './FocusRing';
import styles from './Button.module.css';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      className,
      children,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <FocusRing>
        <button
          ref={ref}
          type={type}
          className={clsx(
            styles.button,
            styles[variant],
            styles[size],
            fullWidth && styles.fullWidth,
            loading && styles.loading,
            className
          )}
          disabled={isDisabled}
          aria-busy={loading}
          {...props}
        >
          {loading ? (
            <Spinner size="sm" className={styles.spinner} />
          ) : (
            icon && iconPosition === 'left' && <span className={styles.icon}>{icon}</span>
          )}
          <span className={styles.text}>{children}</span>
          {!loading && icon && iconPosition === 'right' && (
            <span className={styles.icon}>{icon}</span>
          )}
        </button>
      </FocusRing>
    );
  }
);

Button.displayName = 'Button';
